import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { mdiDelete, mdiPlus, mdiShieldAccountOutline } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import {
    useCampaign,
    useCampaignPropertyOptions,
    useCreateCampaign,
    useUpdateCampaign,
    type Campaign,
    type CampaignApiError,
    type CampaignGroupingInput,
    type CampaignInput,
    type CampaignLevel,
} from '../../hooks/useCampaigns'
import { useAssetTypes } from '../../hooks/useQueries'
import { SECTION_ICONS } from '../../lib/section-icons'
import { StepList, type StepDef } from '../assemblies/StepList'
import { PageHeader } from '../shared/PageHeader'
import { Alert, AlertDescription } from '../ui/alert'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Skeleton } from '../ui/skeleton'
import { AssetTypePicker } from '../asset-type-picker/AssetTypePicker'
import { AgentsPicker, ScopePicker, type CampaignAgent, type ScopeAsset } from './CampaignPickers'
import { campaignInputError } from './campaignInputError'

const LEVELS: CampaignLevel[] = ['site', 'building', 'unit']
const LEVEL_TYPE: Record<CampaignLevel, string> = { site: 'building_group', building: 'building', unit: 'unit' }
const ANSWER_TYPES = ['text', 'number', 'date', 'boolean', 'enum'] as const

interface Question {
    key: string
    label: string
    answer_type: (typeof ANSWER_TYPES)[number]
    options: string
    required: boolean
}

interface GroupingState {
    key: string
    name: string
    typeCode: string
    /** Selected properties and whether each is required. null = take the pre-selection when the options load. */
    properties: Record<string, { required: boolean }> | null
    questions: Question[]
}

let counter = 0
const nextKey = () => `k${++counter}`

function fromCampaign(c: Campaign): GroupingState[] {
    return c.groupings.map((g) => ({
        key: nextKey(),
        name: g.name,
        typeCode: g.asset_type_codes[0] ?? '',
        properties: Object.fromEntries(
            g.items.filter((it) => it.kind === 'property' && it.property_uid).map((it) => [it.property_uid as string, { required: it.required }]),
        ),
        questions: g.items
            .filter((it) => it.kind === 'question')
            .map((it) => ({
                key: nextKey(),
                label: it.label.fr || it.label.en || '',
                answer_type: (it.data_type as Question['answer_type']) || 'text',
                options: it.options.map((o) => o.value).join(', '),
                required: it.required,
            })),
    }))
}

function toInput(groupings: GroupingState[]): CampaignGroupingInput[] {
    return groupings.map((g) => ({
        name: g.name,
        asset_type_codes: g.typeCode ? [g.typeCode] : [],
        items: [
            ...Object.entries(g.properties ?? {}).map(([uid, p]) => ({ kind: 'property' as const, property_uid: uid, required: p.required })),
            ...g.questions.map((q) => ({
                kind: 'question' as const,
                label: q.label,
                answer_type: q.answer_type,
                options: q.answer_type === 'enum' ? q.options.split(',').map((o) => o.trim()).filter(Boolean) : [],
                required: q.required,
            })),
        ],
    }))
}

/** /$org/campaigns/new: the creation wizard. */
export function NewCampaignPage() {
    const { org } = useParams({ from: '/$org/campaigns/new' })
    const { t } = useTranslation('campaigns')
    useBreadcrumbs([{ title: t('title'), path: `/${org}/campaigns` }, { title: t('new') }])
    return <CampaignWizard org={org} />
}

/** /$org/campaigns/$campaignUid/edit: the same wizard on a draft. */
export function EditCampaignPage() {
    const { org, campaignUid } = useParams({ from: '/$org/campaigns/$campaignUid/edit' })
    const { t } = useTranslation('campaigns')
    const { data: campaign, isLoading } = useCampaign(org, campaignUid)
    useBreadcrumbs([
        { title: t('title'), path: `/${org}/campaigns` },
        { title: campaign?.name ?? '', path: `/${org}/campaigns/${campaignUid}` },
        { title: t('edit_questionnaire') },
    ])
    if (isLoading || !campaign) return <Skeleton className="h-64 w-full" />
    return <CampaignWizard org={org} campaign={campaign} />
}

/**
 * The creation wizard (spec 2026-10-20-12 §4), three steps one open at a time
 * (patterns §41): the perimeter and the level, the groupings and what is
 * surveyed in each (the mandatory properties of the type come pre-checked,
 * personal data is flagged), the agents and the due date.
 */
function CampaignWizard({ org, campaign }: { org: string; campaign?: Campaign }) {
    const { t } = useTranslation('campaigns')
    const navigate = useNavigate()
    const create = useCreateCampaign(org)
    const update = useUpdateCampaign(org, campaign?.uid ?? '')
    const [open, setOpen] = useState<string | undefined>('scope')
    const [name, setName] = useState(campaign?.name ?? '')
    const [level, setLevel] = useState<CampaignLevel>(campaign?.level ?? 'building')
    const [scope, setScope] = useState<ScopeAsset[]>(campaign?.scope.map((a) => ({ uid: a.uid, name: a.name })) ?? [])
    const [groupings, setGroupings] = useState<GroupingState[]>(() =>
        campaign ? fromCampaign(campaign) : [{ key: nextKey(), name: t('wizard.default_grouping'), typeCode: LEVEL_TYPE.building, properties: null, questions: [] }],
    )
    const [agents, setAgents] = useState<CampaignAgent[]>(campaign?.agent_list.map((a) => ({ uid: a.uid, name: a.name || a.email || a.uid })) ?? [])
    const [dueOn, setDueOn] = useState(campaign?.due_on ?? '')
    const [dataYear, setDataYear] = useState(String(campaign?.data_year ?? new Date().getFullYear() - 1))
    const [error, setError] = useState<string | null>(null)

    const changeLevel = (next: CampaignLevel) => {
        setLevel(next)
        // The first grouping follows the level while it still surveys the level's type.
        setGroupings((gs) => gs.map((g, i) => (i === 0 && g.typeCode === LEVEL_TYPE[level] ? { ...g, typeCode: LEVEL_TYPE[next], properties: null } : g)))
    }
    const patch = (key: string, change: Partial<GroupingState>) => setGroupings((gs) => gs.map((g) => (g.key === key ? { ...g, ...change } : g)))

    const itemCount = groupings.reduce((n, g) => n + Object.keys(g.properties ?? {}).length + g.questions.length, 0)
    const steps: StepDef[] = [
        {
            id: 'scope',
            title: t('wizard.step_scope'),
            status: name.trim() && scope.length > 0 ? 'done' : 'todo',
            summary: scope.length ? `${t(`level.${level}`)} · ${scope.map((s) => s.name).join(', ')}` : t('scope_empty'),
            content: (
                <div className="space-y-4">
                    <div className="space-y-1">
                        <Label htmlFor="campaign-name">{t('name')}</Label>
                        <Input id="campaign-name" value={name} onChange={(e) => setName(e.target.value)} aria-invalid={!name.trim()} data-testid="campaign-wizard-name" />
                    </div>
                    <div className="space-y-1">
                        <Label>{t('level_label')}</Label>
                        <div className="flex flex-wrap gap-2" role="radiogroup">
                            {LEVELS.map((l) => (
                                <Button key={l} size="sm" role="radio" aria-checked={level === l} variant={level === l ? 'default' : 'outline'}
                                    disabled={!!campaign && campaign.status !== 'draft'} onClick={() => changeLevel(l)} data-testid={`campaign-wizard-level-${l}`}>
                                    {t(`level.${l}`)}
                                </Button>
                            ))}
                        </div>
                        <p className="text-xs text-muted-foreground">{t('wizard.level_hint')}</p>
                    </div>
                    <div className="space-y-1">
                        <Label>{t('scope')}</Label>
                        <p className="text-xs text-muted-foreground" data-testid="campaign-wizard-scope-hint">{t('wizard.scope_hint')}</p>
                        <ScopePicker org={org} value={scope} onChange={setScope} testId="campaign-wizard-scope" />
                    </div>
                </div>
            ),
        },
        {
            id: 'questionnaire',
            title: t('wizard.step_questionnaire'),
            status: itemCount > 0 ? 'done' : 'todo',
            summary: t('wizard.questionnaire_summary', { groupings: groupings.length, items: itemCount }),
            content: (
                <div className="space-y-4">
                    {groupings.map((g, i) => (
                        <GroupingEditor key={g.key} org={org} index={i} grouping={g} onChange={(change) => patch(g.key, change)}
                            onRemove={groupings.length > 1 ? () => setGroupings((gs) => gs.filter((x) => x.key !== g.key)) : undefined} />
                    ))}
                    <Button variant="outline" size="sm"
                        onClick={() => setGroupings((gs) => [...gs, { key: nextKey(), name: '', typeCode: '', properties: null, questions: [] }])}
                        data-testid="campaign-wizard-add-grouping">
                        <AppIcon path={mdiPlus} size="sm" className="mr-1" />
                        {t('wizard.add_grouping')}
                    </Button>
                </div>
            ),
        },
        {
            id: 'agents',
            title: t('wizard.step_agents'),
            status: agents.length > 0 ? 'done' : 'todo',
            summary: t('agents_count', { count: agents.length }) + (dueOn ? ` · ${dueOn}` : ''),
            content: (
                <div className="space-y-4">
                    <div className="space-y-1">
                        <Label>{t('agents')}</Label>
                        <AgentsPicker org={org} value={agents} onChange={setAgents} testId="campaign-wizard-agents" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                            <Label htmlFor="campaign-due">{t('due_date')}</Label>
                            <Input id="campaign-due" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} data-testid="campaign-wizard-due" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="campaign-year">{t('data_year')}</Label>
                            <Input id="campaign-year" type="number" value={dataYear} onChange={(e) => setDataYear(e.target.value)} data-testid="campaign-wizard-year" />
                        </div>
                    </div>
                </div>
            ),
        },
    ]

    const submit = async () => {
        setError(null)
        const body: CampaignInput = {
            name: name.trim(),
            level,
            due_on: dueOn,
            data_year: dataYear ? Number(dataYear) : undefined,
            scope_asset_uids: scope.map((s) => s.uid),
            agent_uids: agents.map((a) => a.uid),
            groupings: toInput(groupings),
        }
        try {
            const saved = campaign ? await update.mutateAsync(body) : await create.mutateAsync(body)
            await navigate({ to: '/$org/campaigns/$campaignUid', params: { org, campaignUid: saved.uid }, search: { tab: 'settings' } })
        } catch (e) {
            const { key, n } = campaignInputError((e as CampaignApiError).fields?.[0]?.name)
            setError(t(key as never, { n }))
        }
    }

    return (
        <div className="space-y-6" data-testid="campaign-wizard">
            <PageHeader icon={SECTION_ICONS.campaigns} title={campaign ? campaign.name : t('new')} description={t('wizard.description')} />
            <StepList steps={steps} open={open} onOpenChange={setOpen} testId="campaign-wizard-steps" />
            {error && (
                <Alert variant="destructive" data-testid="campaign-wizard-error">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}
            <div className="flex justify-end">
                <Button onClick={() => void submit()} disabled={!name.trim() || scope.length === 0 || create.isPending || update.isPending}
                    data-testid="campaign-wizard-submit">
                    {campaign ? t('save') : t('wizard.create')}
                </Button>
            </div>
        </div>
    )
}

function GroupingEditor({ org, index, grouping, onChange, onRemove }: {
    org: string
    index: number
    grouping: GroupingState
    onChange: (change: Partial<GroupingState>) => void
    onRemove?: () => void
}) {
    const { t, i18n } = useTranslation('campaigns')
    const lang = i18n.language.slice(0, 2)
    const { data: assetTypes = [] } = useAssetTypes(org)
    const { data: options = [], isLoading, isSuccess } = useCampaignPropertyOptions(org, grouping.typeCode || undefined)
    useEffect(() => {
        if (grouping.properties === null && grouping.typeCode && isSuccess) {
            onChange({ properties: Object.fromEntries(options.filter((o) => o.preselected).map((o) => [o.uid, { required: true }])) })
        }
    }, [grouping.properties, grouping.typeCode, isSuccess, options, onChange])
    const selected = grouping.properties ?? {}
    const tid = `campaign-wizard-grouping-${index}`
    // A field the API requires reads red (aria-invalid) until it is filled (#1618).

    return (
        <Card data-testid={tid}>
            <CardContent className="space-y-3 pt-4">
                <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-48 flex-1 space-y-1">
                        <Label>{t('wizard.grouping_name')}</Label>
                        <Input value={grouping.name} placeholder={t('wizard.grouping_placeholder')} onChange={(e) => onChange({ name: e.target.value })}
                            aria-invalid={!grouping.name.trim()} data-testid={`${tid}-name`} />
                    </div>
                    <div className="min-w-48 flex-1 space-y-1">
                        <Label>{t('wizard.component_type')}</Label>
                        <AssetTypePicker
                            org={org}
                            value={assetTypes.find((at) => at.code === grouping.typeCode)?.uid ?? ''}
                            onChange={(uid) => {
                                const code = assetTypes.find((at) => at.uid === uid)?.code
                                if (code && code !== grouping.typeCode) onChange({ typeCode: code, properties: null })
                            }}
                            placeholder={t('wizard.choose_type')}
                            ariaLabel={t('wizard.component_type')}
                            invalid={!grouping.typeCode}
                            triggerTestId={`${tid}-type`}
                        />
                    </div>
                    {onRemove && (
                        <Button variant="outline" size="icon" onClick={onRemove} aria-label={t('wizard.remove_grouping')} title={t('wizard.remove_grouping')} data-testid={`${tid}-remove`}>
                            <AppIcon path={mdiDelete} size="sm" />
                        </Button>
                    )}
                </div>
                {grouping.typeCode && (
                    <div className="space-y-1">
                        <p className="text-sm font-medium">{t('wizard.properties')}</p>
                        {isLoading ? (
                            <Skeleton className="h-16 w-full" />
                        ) : options.length === 0 ? (
                            <p className="text-sm text-muted-foreground">{t('wizard.no_property')}</p>
                        ) : (
                            <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
                                {options.map((o) => {
                                    const on = !!selected[o.uid]
                                    return (
                                        <li key={o.uid} className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-sm" data-testid={`${tid}-prop-${o.code}`}>
                                            <Checkbox
                                                checked={on}
                                                onCheckedChange={(next) => {
                                                    const copy = { ...selected }
                                                    if (next) copy[o.uid] = { required: o.requirement === 'mandatory' }
                                                    else delete copy[o.uid]
                                                    onChange({ properties: copy })
                                                }}
                                                data-testid={`${tid}-prop-${o.code}-check`}
                                            />
                                            <span className="min-w-0 flex-1">{o.name[lang] || o.name.fr || o.code}</span>
                                            <Badge variant={o.requirement === 'mandatory' ? 'default' : 'outline'}>{t(`requirement.${o.requirement}`)}</Badge>
                                            {o.personal_data && (
                                                <Badge variant="warning" className="gap-1" title={t('personal_data_hint')} data-testid={`${tid}-prop-${o.code}-pii`}>
                                                    <AppIcon path={mdiShieldAccountOutline} size="xs" />
                                                    {t('personal_data')}
                                                </Badge>
                                            )}
                                            {on && (
                                                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Checkbox
                                                        checked={selected[o.uid].required}
                                                        onCheckedChange={(next) => onChange({ properties: { ...selected, [o.uid]: { required: !!next } } })}
                                                        data-testid={`${tid}-prop-${o.code}-required`}
                                                    />
                                                    {t('required')}
                                                </label>
                                            )}
                                        </li>
                                    )
                                })}
                            </ul>
                        )}
                    </div>
                )}
                <div className="space-y-2">
                    <p className="text-sm font-medium">{t('wizard.questions')}</p>
                    {grouping.questions.map((q, qi) => (
                        <div key={q.key} className="flex flex-wrap items-end gap-2 rounded-md border p-2" data-testid={`${tid}-question-${qi}`}>
                            <Input className="min-w-48 flex-1" value={q.label} placeholder={t('wizard.question_label')} aria-invalid={!q.label.trim()}
                                onChange={(e) => onChange({ questions: grouping.questions.map((x) => (x.key === q.key ? { ...x, label: e.target.value } : x)) })}
                                data-testid={`${tid}-question-${qi}-label`} />
                            <Select value={q.answer_type} onValueChange={(v) => onChange({ questions: grouping.questions.map((x) => (x.key === q.key ? { ...x, answer_type: v as Question['answer_type'] } : x)) })}>
                                <SelectTrigger className="w-36" data-testid={`${tid}-question-${qi}-type`}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ANSWER_TYPES.map((a) => (
                                        <SelectItem key={a} value={a} data-testid={`${tid}-question-${qi}-type-${a}`}>{t(`answer_type.${a}`)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {q.answer_type === 'enum' && (
                                <Input className="min-w-48 flex-1" value={q.options} placeholder={t('wizard.options_placeholder')} aria-invalid={!q.options.trim()}
                                    onChange={(e) => onChange({ questions: grouping.questions.map((x) => (x.key === q.key ? { ...x, options: e.target.value } : x)) })}
                                    data-testid={`${tid}-question-${qi}-options`} />
                            )}
                            <label className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Checkbox checked={q.required}
                                    onCheckedChange={(next) => onChange({ questions: grouping.questions.map((x) => (x.key === q.key ? { ...x, required: !!next } : x)) })}
                                    data-testid={`${tid}-question-${qi}-required`} />
                                {t('required')}
                            </label>
                            <Button variant="outline" size="icon" aria-label={t('wizard.remove_question')} title={t('wizard.remove_question')}
                                onClick={() => onChange({ questions: grouping.questions.filter((x) => x.key !== q.key) })}
                                data-testid={`${tid}-question-${qi}-remove`}>
                                <AppIcon path={mdiDelete} size="sm" />
                            </Button>
                        </div>
                    ))}
                    <Button variant="outline" size="sm"
                        onClick={() => onChange({ questions: [...grouping.questions, { key: nextKey(), label: '', answer_type: 'boolean', options: '', required: false }] })}
                        data-testid={`${tid}-add-question`}>
                        <AppIcon path={mdiPlus} size="sm" className="mr-1" />
                        {t('wizard.add_question')}
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}
